import React, { useState, useEffect, useContext } from "react";
import { Modal, Input, Spin, message } from "antd";
import { AuthContext } from "../../../contexts/AuthContext";
import { useQv4Theme, mapChatItem } from "../../helpers";
import { useQueryLifecycle } from "../../viewmodels/modals-misc/useQueryLifecycle";
import { useChatListPage } from "../../viewmodels/chat/useChatListPage";

// Same single-step forward chain as the backend's CANONICAL_STATUS_ORDER
// (routes/queries.js) — no dropdown, the next stage is always computed, per
// the backend handover for POST /:query_pk/move-status-forward. "cancelled"
// deliberately isn't included: it's never a legitimate forward target (only
// reachable from "completed", which is itself blocked from moving forward
// at all), so it'd never actually be offered.
const STATUS_ORDER = [
  "draft",
  "waiting_for_price",
  "waiting_customer_confirmation",
  "confirmed_waiting_payment",
  "paid",
  "waiting_purchase",
  "purchased",
  "out_for_delivery",
  "delivered",
  "completed",
];

const STATUS_LABELS = {
  draft: "Draft",
  waiting_for_price: "Waiting For Price",
  waiting_customer_confirmation: "Waiting Confirmation",
  confirmed_waiting_payment: "Confirmed · Awaiting Payment",
  paid: "Paid",
  waiting_purchase: "Waiting Purchase (Ops)",
  purchased: "Purchased (Ops)",
  out_for_delivery: "Out For Delivery (Ops)",
  delivered: "Delivered",
  completed: "Completed",
};

// Returns null for anything not on the forward chain at all (cancelled,
// disabled, parked, on_hold, postponed) as well as "completed" itself (no
// next stage) — WorkflowCard.js uses that single null check to decide
// whether to show "Move to Next Stage" at all, matching the backend's own
// (separate) rejection of those same statuses.
export function nextForwardStatus(currentStatus) {
  const idx = STATUS_ORDER.indexOf(currentStatus);
  if (idx < 0 || idx === STATUS_ORDER.length - 1) return null;
  return STATUS_ORDER[idx + 1];
}

const CHECK = { CHECKING: "checking", CLEAR: "clear", BLOCKED: "blocked", ERROR: "error" };

// Sent as the reason on every skipWatiCheck (subsequent-stage) move — the
// backend requires a non-empty reason on every call regardless, but the
// user isn't re-prompted for one past the initial gate (see the module
// comment below).
const CONTINUATION_REASON = "Continuing manual forward progression — customer's lack of WATI availability was already confirmed for this task at the Waiting For Price stage.";

// Pre-filled (editable) reason for the draft → waiting_for_price skip —
// see the isDraftSkip comment below. Removes typing friction for the
// common case; staff can still overwrite it with specifics.
// Exported so the other two skip-suppliers entry points (SendToSuppliersModal,
// EditDraftDrawer) that don't open this modal at all send the same audit text.
export const SKIP_SUPPLIERS_REASON = "Recent quote on file found, skipping sending to suppliers";

// Manual forward movement is normally only for customers reached through a
// channel other than WATI (fleet owners on a dedicated phone line, etc.) —
// normal progress otherwise happens via WATI-driven business actions. The
// WATI check + mandatory reason only ever happen once per task, at
// waiting_for_price (see WorkflowCard.js's visibility condition, which
// gates that stage on parts_with_quotes too — no point running the check
// before there's even a quote to send). `skipWatiCheck` is how the caller
// signals "this task already passed that gate" (card.ever_manually_forwarded
// was true) — every later stage becomes a lightweight one-click confirm
// instead: no WATI search, no reason prompt, a fixed reason sent instead.
//
// draft → waiting_for_price is a second, deliberately separate exemption
// (isDraftSkip below): staff who already have a recent trusted supplier
// price on file (see the price-history feature) shouldn't have to prove
// the customer is unreachable on WATI just to skip a redundant supplier
// broadcast — this has nothing to do with WATI reachability at all, so it
// gets its own copy and a pre-filled (not canned/hidden) reason, rather
// than reusing skipWatiCheck's semantics.
export function MoveForwardModal({ open, queryPk, queryId, currentStatus, customerPhone, skipWatiCheck, onClose, onSuccess }) {
  const { authState } = useContext(AuthContext);
  const { t } = useQv4Theme();
  const { moveForward } = useQueryLifecycle(queryPk, authState?.id);
  const { searchByPhone } = useChatListPage();
  const isDraftSkip = currentStatus === "draft";
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkState, setCheckState] = useState((skipWatiCheck || isDraftSkip) ? CHECK.CLEAR : CHECK.CHECKING);

  const toStatus = nextForwardStatus(currentStatus);

  useEffect(() => {
    if (!open) return;
    setReason(isDraftSkip ? SKIP_SUPPLIERS_REASON : "");
    setSaving(false);
    if (skipWatiCheck || isDraftSkip) { setCheckState(CHECK.CLEAR); return; }
    setCheckState(CHECK.CHECKING);
    const phone = (customerPhone || "").replace(/\D/g, "");
    if (!phone) { setCheckState(CHECK.ERROR); return; }
    let cancelled = false;
    searchByPhone(authState?.id, phone).then(({ list, success }) => {
      if (cancelled) return;
      if (!success) { setCheckState(CHECK.ERROR); return; }
      const hit = list.map(mapChatItem).some(c => (c.phone || "").replace(/\D/g, "") === phone);
      setCheckState(hit ? CHECK.BLOCKED : CHECK.CLEAR);
    }).catch(() => { if (!cancelled) setCheckState(CHECK.ERROR); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerPhone, authState?.id, skipWatiCheck, isDraftSkip]);

  const handleClose = () => {
    setReason("");
    onClose();
  };

  const handleOk = async () => {
    if (checkState !== CHECK.CLEAR || !toStatus) return;
    const finalReason = skipWatiCheck ? CONTINUATION_REASON : reason.trim();
    if (!skipWatiCheck && !finalReason) { message.warning("A reason is required — it becomes part of the task's movement history"); return; }
    setSaving(true);
    try {
      const res = await moveForward(toStatus, finalReason);
      if (!res?.success) throw new Error(res?.message);
      message.success(`Task moved forward to ${STATUS_LABELS[toStatus] ?? toStatus}`);
      onSuccess?.(res.data?.task);
      handleClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to move task forward");
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      title={`Move to Next Stage${queryId ? ` — #${queryId}` : ""}`}
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Move Forward"
      cancelText="Close"
      okButtonProps={{ disabled: checkState !== CHECK.CLEAR || !toStatus }}
      destroyOnHidden
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
        {!toStatus ? (
          <div style={{ color: "#ff4d4f" }}>This task has no valid next stage to move to.</div>
        ) : isDraftSkip ? (
          <>
            <div style={{ fontSize: 13, color: t.textSub }}>
              Skip sending this to suppliers and move it straight to{" "}
              <b>{STATUS_LABELS[toStatus] ?? toStatus}</b>? Recorded in the task's movement
              history with your name and reason. Nothing gets sent to any supplier.
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
                Reason <span style={{ color: "#ff4d4f" }}>*</span>
              </div>
              <Input.TextArea
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </div>
          </>
        ) : skipWatiCheck ? (
          <div style={{ fontSize: 13, color: t.textSub }}>
            Move from <b>{STATUS_LABELS[currentStatus] ?? currentStatus}</b> to{" "}
            <b>{STATUS_LABELS[toStatus] ?? toStatus}</b>? Already confirmed off-WATI for this task
            at the Waiting For Price stage — no re-check needed.
          </div>
        ) : checkState === CHECK.CHECKING ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: t.textSub }}>
            <Spin size="small" /> Checking WATI availability for this customer…
          </div>
        ) : checkState === CHECK.ERROR ? (
          <div style={{ color: "#ff4d4f" }}>
            Couldn't verify WATI availability for this customer — try again in a moment.
          </div>
        ) : checkState === CHECK.BLOCKED ? (
          <div style={{ color: "#c5871c", fontSize: 13 }}>
            This customer has an active WATI conversation. Manual forward movement is only meant
            for customers reached through a different channel — please use the normal WATI-driven
            flow to progress this task instead.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: t.textSub }}>
              Moving from <b>{STATUS_LABELS[currentStatus] ?? currentStatus}</b> to{" "}
              <b>{STATUS_LABELS[toStatus] ?? toStatus}</b>. This is an exception path for customers
              reached outside WATI — the move is recorded in the task's movement history with your
              name and reason. If required data (e.g. a selected quote, a recorded payment) is
              missing for this stage, the request will be rejected with a specific reason.
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
                Reason <span style={{ color: "#ff4d4f" }}>*</span>
              </div>
              <Input.TextArea
                rows={3}
                placeholder="e.g. Customer confirmed through the dedicated phone line."
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
